import { useLanguage } from "@/contexts/LanguageContext";
import { useUser } from "@/lib/useUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Video, Calendar, Copy, ExternalLink, Clock, Users, Plus } from "lucide-react";
import { useState } from "react";
import { NavLink } from "react-router-dom";

// ── Types ────────────────────────────────────────────────────────────────────
type Role = "lender" | "buyer" | "seller" | "broker" | "consultant" | "partner" | "admin";

interface Props {
  role: Role;
}

interface Meeting {
  id: string;
  titleEn: string;
  titleFr: string;
  date: string;
  time: string;
  durationMin: number;
  participants: string[];
  link: string;
}

// ── Role config ───────────────────────────────────────────────────────────────
const ROLE_CONFIG: Record<Role, {
  portalEn: string;
  portalFr: string;
  calendarHref: string;
  defaultContactsEn: string[];
  defaultContactsFr: string[];
}> = {
  lender: {
    portalEn: "Lender Portal", portalFr: "Portail Prêteur",
    calendarHref: "/dashboard/lender/calendar",
    defaultContactsEn: ["AcquizGroup Coordinator", "Deal Coordinator"],
    defaultContactsFr: ["Coordinateur AcquizGroup", "Coordinateur de deal"],
  },
  buyer: {
    portalEn: "Buyer Portal", portalFr: "Portail Acheteur",
    calendarHref: "/dashboard/buyer/calendar",
    defaultContactsEn: ["AcquizGroup Coordinator", "Acquisition Advisor"],
    defaultContactsFr: ["Coordinateur AcquizGroup", "Conseiller en acquisition"],
  },
  seller: {
    portalEn: "Seller Portal", portalFr: "Portail Vendeur",
    calendarHref: "/dashboard/seller/calendar",
    defaultContactsEn: ["AcquizGroup Coordinator", "Listing Advisor"],
    defaultContactsFr: ["Coordinateur AcquizGroup", "Conseiller en inscription"],
  },
  broker: {
    portalEn: "Broker Portal", portalFr: "Portail Courtier",
    calendarHref: "/dashboard/broker/calendar",
    defaultContactsEn: ["AcquizGroup Admin", "Deal Support"],
    defaultContactsFr: ["Admin AcquizGroup", "Support deal"],
  },
  consultant: {
    portalEn: "Consultant Portal", portalFr: "Portail Consultant",
    calendarHref: "/dashboard/consultant/calendar",
    defaultContactsEn: ["AcquizGroup Admin", "Client Coordinator"],
    defaultContactsFr: ["Admin AcquizGroup", "Coordinateur client"],
  },
  partner: {
    portalEn: "Partner Portal", portalFr: "Portail Partenaire",
    calendarHref: "/dashboard/partner/calendar",
    defaultContactsEn: ["AcquizGroup Admin", "Partner Support"],
    defaultContactsFr: ["Admin AcquizGroup", "Support partenaire"],
  },
  admin: {
    portalEn: "Admin Portal", portalFr: "Portail Admin",
    calendarHref: "/dashboard/admin/calendar",
    defaultContactsEn: ["AcquizGroup Team"],
    defaultContactsFr: ["Équipe AcquizGroup"],
  },
};

// ── Demo meetings (remplacées par Firestore en prod) ─────────────────────────
const DEMO_MEETINGS: Meeting[] = [
  {
    id: "1",
    titleEn: "Deal Review Session",
    titleFr: "Séance de révision de deal",
    date: "2026-06-13",
    time: "10:00",
    durationMin: 45,
    participants: ["AcquizGroup Coordinator", "Deal Coordinator"],
    link: "https://meet.google.com/xxx-yyyy-zzz",
  },
  {
    id: "2",
    titleEn: "Onboarding Call",
    titleFr: "Appel d'intégration",
    date: "2026-06-16",
    time: "14:30",
    durationMin: 30,
    participants: ["AcquizGroup Admin"],
    link: "https://meet.google.com/aaa-bbbb-ccc",
  },
  {
    id: "3",
    titleEn: "Quarterly Portfolio Review",
    titleFr: "Révision trimestrielle du portefeuille",
    date: "2026-05-20",
    time: "11:00",
    durationMin: 60,
    participants: ["AcquizGroup Coordinator"],
    link: "https://meet.google.com/zzz-aaaa-bbb",
  },
];

// ── Translations ──────────────────────────────────────────────────────────────
const T = {
  en: {
    title: "Video Conference",
    subtitle: "Scheduled meetings with your AcquizGroup team",
    joinMeeting: "Join a Meeting",
    enterLink: "Enter a meeting link or code",
    join: "Join",
    upcoming: "Upcoming",
    past: "Past Meetings",
    noUpcoming: "No upcoming meetings scheduled",
    noUpcomingSub: "Your coordinator will schedule meetings as needed",
    submitted: "Submitted",
    duration: "min",
    copyLink: "Copy link",
    copied: "Copied!",
    calendar: "Calendar",
    requestMeeting: "Request a Meeting",
    participants: "Participants",
  },
  fr: {
    title: "Visioconférence",
    subtitle: "Réunions planifiées avec votre équipe AcquizGroup",
    joinMeeting: "Rejoindre une réunion",
    enterLink: "Entrez un lien ou code de réunion",
    join: "Rejoindre",
    upcoming: "À venir",
    past: "Réunions passées",
    noUpcoming: "Aucune réunion planifiée",
    noUpcomingSub: "Votre coordinateur planifiera des réunions au besoin",
    submitted: "Soumise",
    duration: "min",
    copyLink: "Copier le lien",
    copied: "Copié !",
    calendar: "Calendrier",
    requestMeeting: "Demander une réunion",
    participants: "Participants",
  },
};

// ── Component ─────────────────────────────────────────────────────────────────
export default function VideoConference({ role }: Props) {
  const { language } = useLanguage();
  const isFr = language === "fr";
  const t = isFr ? T.fr : T.en;
  const cfg = ROLE_CONFIG[role];

  const [joinLink, setJoinLink] = useState("");
  const [copied, setCopied] = useState<string | null>(null);

  const now = new Date();
  const upcoming = DEMO_MEETINGS.filter(
    m => new Date(`${m.date}T${m.time}`) >= now
  );
  const past = DEMO_MEETINGS.filter(
    m => new Date(`${m.date}T${m.time}`) < now
  );

  function copyLink(id: string, link: string) {
    navigator.clipboard.writeText(link).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  function handleJoin() {
    if (!joinLink.trim()) return;
    const url = joinLink.startsWith("http") ? joinLink : `https://meet.google.com/${joinLink}`;
    window.open(url, "_blank", "noopener,noreferrer");
  }

  function fmtDate(date: string, time: string) {
    return new Date(`${date}T${time}`).toLocaleDateString(
      isFr ? "fr-CA" : "en-US",
      { weekday: "short", month: "short", day: "numeric" }
    );
  }

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-medium" style={{ color: "#1D314D" }}>
            {t.title}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <NavLink to={cfg.calendarHref}>
            <Button variant="outline" size="sm" className="gap-2">
              <Calendar className="h-4 w-4" />
              {t.calendar}
            </Button>
          </NavLink>
          <Button
            size="sm"
            className="gap-2"
            style={{ background: "#1D314D", color: "#CEAB65", border: "1px solid rgba(206,171,101,0.3)" }}
            onClick={() => {
              const subject = encodeURIComponent(isFr ? "Demande de réunion — AcquizGroup" : "Meeting Request — AcquizGroup");
              window.location.href = `mailto:support@acquizgroup.com?subject=${subject}`;
            }}
          >
            <Plus className="h-4 w-4" />
            {t.requestMeeting}
          </Button>
        </div>
      </div>

      {/* ── Quick join ─────────────────────────────────────────────────── */}
      <Card style={{ border: "0.5px solid rgba(29,49,77,0.15)", background: "rgba(29,49,77,0.02)" }}>
        <CardContent className="p-5">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "rgba(206,171,101,0.12)", border: "1px solid rgba(206,171,101,0.25)" }}
            >
              <Video className="h-4 w-4" style={{ color: "#CEAB65" }} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: "#1D314D" }}>{t.joinMeeting}</p>
              <p className="text-xs text-muted-foreground">{t.enterLink}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={joinLink}
              onChange={e => setJoinLink(e.target.value)}
              onKeyDown={e => e.key === "Enter" && handleJoin()}
              placeholder="https://meet.google.com/abc-defg-hij"
              className="flex-1 text-sm border rounded-md px-3 py-2 outline-none focus:ring-1"
              style={{
                borderColor: "var(--color-border-tertiary)",
                background: "var(--color-background-primary)",
              }}
            />
            <Button
              size="sm"
              onClick={handleJoin}
              disabled={!joinLink.trim()}
              className="gap-2"
              style={{ background: "#1D314D", color: "white" }}
            >
              <ExternalLink className="h-4 w-4" />
              {t.join}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Default contacts ───────────────────────────────────────────── */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-xs"
        style={{ background: "var(--color-background-secondary)", color: "var(--color-text-secondary)" }}
      >
        <Users className="h-3.5 w-3.5 flex-shrink-0" />
        <span>
          {isFr ? "Contacts disponibles : " : "Available contacts: "}
          <strong style={{ color: "var(--color-text-primary)" }}>
            {(isFr ? cfg.defaultContactsFr : cfg.defaultContactsEn).join(" · ")}
          </strong>
        </span>
      </div>

      {/* ── Upcoming meetings ──────────────────────────────────────────── */}
      <div>
        <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-3">
          {t.upcoming}
        </h2>

        {upcoming.length === 0 ? (
          <Card>
            <CardContent className="py-14 text-center">
              <Video className="h-9 w-9 mx-auto mb-3 text-muted-foreground/25" />
              <p className="text-sm font-medium text-muted-foreground">{t.noUpcoming}</p>
              <p className="text-xs text-muted-foreground mt-1">{t.noUpcomingSub}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {upcoming.map(m => (
              <Card key={m.id} className="hover:shadow-sm transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {/* Gold accent bar + title */}
                      <div className="flex items-center gap-2.5">
                        <div
                          className="w-0.5 h-8 rounded-full flex-shrink-0"
                          style={{ background: "#CEAB65" }}
                        />
                        <div>
                          <p className="text-sm font-medium" style={{ color: "#1D314D" }}>
                            {isFr ? m.titleFr : m.titleEn}
                          </p>
                          <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {fmtDate(m.date, m.time)} · {m.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {m.durationMin} {t.duration}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {m.participants.join(", ")}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button
                        onClick={() => copyLink(m.id, m.link)}
                        className="text-xs flex items-center gap-1 px-2.5 py-1.5 rounded-md border transition-all"
                        style={{
                          borderColor: "var(--color-border-tertiary)",
                          color: "var(--color-text-secondary)",
                          background: "var(--color-background-primary)",
                        }}
                      >
                        <Copy className="h-3 w-3" />
                        {copied === m.id ? t.copied : t.copyLink}
                      </button>
                      <a href={m.link} target="_blank" rel="noopener noreferrer">
                        <Button
                          size="sm"
                          className="gap-1.5 h-8 text-xs"
                          style={{ background: "#1D314D", color: "#CEAB65", border: "1px solid rgba(206,171,101,0.25)" }}
                        >
                          <Video className="h-3 w-3" />
                          {t.join}
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ── Past meetings ──────────────────────────────────────────────── */}
      {past.length > 0 && (
        <div>
          <h2 className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-3">
            {t.past}
          </h2>
          <div className="space-y-2">
            {past.map(m => (
              <div
                key={m.id}
                className="flex items-center justify-between px-4 py-3 rounded-lg"
                style={{ background: "var(--color-background-secondary)", opacity: 0.65 }}
              >
                <div>
                  <p className="text-sm">{isFr ? m.titleFr : m.titleEn}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {fmtDate(m.date, m.time)} · {m.durationMin} {t.duration}
                  </p>
                </div>
                <span className="text-[10px] text-muted-foreground italic">
                  {t.past}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
